import React, { useEffect, useState } from "react";
import {
  MapPin,
  BriefcaseBusiness,
  ArrowRight,
  ShieldCheck
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../../../api";
import "./professional.suggestions.css";

export default function ProfessionalSuggestions() {
  const navigate = useNavigate();

  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);

  /* =====================================================
     LOAD RECOMMENDED PROFESSIONALS
  ===================================================== */

  useEffect(() => {
    const loadProfessionals = async () => {
      try {
        const res = await API.get(
          "/profession/recommended-professionals"
        );

        if (res.data.success) {
          setProfessionals(
            res.data.professionals || []
          );
        }
      } catch (err) {
        console.error(
          "Failed to load recommended professionals:",
          err
        );
      } finally {
        setLoading(false);
      }
    };

    loadProfessionals();
  }, []);


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <div className="suggestions-loading">

        <div className="suggestion-skeleton" />

        <div className="suggestion-skeleton" />

      </div>
    );
  }


  /* =====================================================
     EMPTY
  ===================================================== */

  if (!professionals.length) {
    return (
      <div className="suggestions-empty">

        <div className="suggestions-empty-icon">
          👥
        </div>

        <strong>
          No recommendations yet
        </strong>

        <span>
          More professionals will appear as
          the Civilink network grows.
        </span>

      </div>
    );
  }


  /* =====================================================
     PROFESSIONAL CARDS
  ===================================================== */

  return (
    <div className="suggestions-list">

      {professionals.map((professional) => {

        const initials =
          professional.name
            ?.trim()
            ?.charAt(0)
            ?.toUpperCase() || "?";


        return (
          <article
            key={professional._id}
            className="professional-suggestion-card"
          >

            {/* =========================================
                PROFILE PHOTO
            ========================================= */}

            <div className="suggestion-avatar">

              {professional.profilePhoto ? (

                <img
                  src={professional.profilePhoto}
                  alt=""
                />

              ) : (

                <span>
                  {initials}
                </span>

              )}

            </div>


            {/* =========================================
                PROFESSIONAL INFORMATION
            ========================================= */}

            <div className="suggestion-content">

              {/* NAME */}

              <div className="suggestion-name-row">

                <h3>
                  {professional.name}
                </h3>

                <ShieldCheck
                  size={15}
                  className="suggestion-verified"
                  aria-label="Verified professional"
                />

              </div>


              {/* PROFESSION */}

              <p className="suggestion-profession">
                {professional.profession}
              </p>


              {/* =======================================
                  LOCATION + EXPERIENCE
              ======================================= */}

              <div className="suggestion-meta">

                {professional.location?.city && (

                  <span>

                    <MapPin size={13} />

                    {professional.location.city}

                  </span>

                )}


                <span>

                  <BriefcaseBusiness size={13} />

                  {professional.experienceYears || 0}{" "}

                  {professional.experienceYears === 1
                    ? "year"
                    : "years"}

                </span>

              </div>


              {/* =======================================
                  SKILLS
              ======================================= */}

              {professional.skills?.length > 0 && (

                <div className="suggestion-skills">

                  {professional.skills
                    .slice(0, 3)
                    .map((skill, index) => (

                      <span
                        key={`${skill}-${index}`}
                        className="suggestion-skill"
                      >
                        {skill}
                      </span>

                    ))}


                  {professional.skills.length > 3 && (

                    <span className="suggestion-more">
                      +{professional.skills.length - 3}
                    </span>

                  )}

                </div>

              )}


              {/* =======================================
                  RECOMMENDATION REASON
              ======================================= */}

              {professional.reason && (

                <p className="suggestion-reason">
                  {professional.reason}
                </p>

              )}

            </div>


            {/* =========================================
                VIEW PROFILE
            ========================================= */}

            <button
              type="button"
              className="suggestion-arrow"
              onClick={() =>
                navigate(
                  `/profile/${professional._id}`
                )
              }
              aria-label={`View ${professional.name}'s profile`}
            >

              <ArrowRight size={17} />

            </button>

          </article>
        );
      })}

    </div>
  );
}